import { useCallback, useEffect, useRef, useState } from 'react'
import {
  listBenefitRuleActions,
  listBenefitRuleFields,
  listBenefitRuleFieldValues,
  listBenefitRuleOperators,
  type BenefitRuleActionMasterRecord,
  type BenefitRuleFieldMasterRecord,
  type BenefitRuleFieldValueMasterRecord,
  type BenefitRuleOperatorMasterRecord,
} from '../services/benefitRuleMastersService'

export function useBenefitRuleMasters(enabled = true) {
  const [fields, setFields] = useState<BenefitRuleFieldMasterRecord[]>([])
  const [operators, setOperators] = useState<BenefitRuleOperatorMasterRecord[]>([])
  const [actions, setActions] = useState<BenefitRuleActionMasterRecord[]>([])
  const [fieldValuesByFieldId, setFieldValuesByFieldId] = useState<
    Record<string, BenefitRuleFieldValueMasterRecord[]>
  >({})
  const fieldValuesRef = useRef<Record<string, BenefitRuleFieldValueMasterRecord[]>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadMasters = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    setError(null)
    try {
      const [fieldRows, operatorRows, actionRows] = await Promise.all([
        listBenefitRuleFields(),
        listBenefitRuleOperators(),
        listBenefitRuleActions(),
      ])
      setFields(fieldRows.filter((row) => row.status === 'active'))
      setOperators(operatorRows.filter((row) => row.status === 'active'))
      setActions(actionRows.filter((row) => row.status === 'active'))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load benefit rule masters.')
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    void loadMasters()
  }, [loadMasters])

  const loadFieldValues = useCallback(async (fieldMasterId: string) => {
    const id = fieldMasterId.trim()
    if (!id) return []
    if (fieldValuesRef.current[id]) return fieldValuesRef.current[id]

    try {
      const rows = (await listBenefitRuleFieldValues(id)).filter((row) => row.status === 'active')
      fieldValuesRef.current[id] = rows
      setFieldValuesByFieldId((prev) => ({ ...prev, [id]: rows }))
      return rows
    } catch {
      return []
    }
  }, [])

  return {
    fields,
    operators,
    actions,
    fieldValuesByFieldId,
    loading,
    error,
    reload: loadMasters,
    loadFieldValues,
    hasMasters: fields.length > 0 && operators.length > 0 && actions.length > 0,
  }
}
